'use client';

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { heroBackgrounds } from './heroBackgrounds';

/** How long each background stays on screen before auto-advancing (ms). */
const SLIDE_DURATION = 6000;

const IMAGE_SIZES =
  '(min-width: 1780px) 1584px, (min-width: 1440px) calc(45vw + 792px), 1344px';

/**
 * Cycles the hero background while the copy on top stays put.
 * Renders two pieces the parent positions: the stacked backgrounds and the
 * bottom-right controls (dots + prev/next). Autoplay pauses on hover/focus,
 * when the tab is hidden, and is disabled entirely for reduced-motion users.
 */
const useHeroSlider = (count: number) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const goTo = useCallback(
    (index: number) => setActive(((index % count) + count) % count),
    [count]
  );
  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const autoplay = !reducedMotion && count > 1 && !paused;

  useEffect(() => {
    if (!autoplay) return;
    const id = window.setTimeout(next, SLIDE_DURATION);
    return () => window.clearTimeout(id);
  }, [autoplay, active, next]);

  return { active, goTo, next, prev, setPaused };
};

export const HeroBackgrounds = ({ active }: { active: number }) => (
  <>
    {heroBackgrounds.map((bg, i) => {
      const isActive = i === active;
      return (
        <div
          key={i}
          aria-hidden={!isActive}
          className={classNames(
            'absolute inset-0 overflow-hidden transition-opacity duration-1000 ease-out',
            isActive ? 'opacity-100' : 'opacity-0'
          )}
        >
          <Image
            src={bg.image}
            alt={bg.alt}
            quality={100}
            fill
            className={classNames(
              'object-cover transition-transform duration-[1600ms] ease-out',
              isActive ? 'scale-100' : 'scale-105'
            )}
            loading={i === 0 ? 'eager' : 'lazy'}
            priority={i === 0}
            sizes={IMAGE_SIZES}
            unoptimized
          />
          {bg.dim && <div className="home-header-gradient" />}
        </div>
      );
    })}
  </>
);

/**
 * Geometry lifted verbatim from the Figma export (paginator.svg): a 50.58px
 * circle filled with #89D9F7 at 32%, and a thin 12x21.6 chevron. The glyph is
 * drawn in the button's own 50.58 coordinate space so it scales with it.
 */
const BUTTON_SIZE = 50.5757;
const CHEVRON_PATHS = {
  prev: 'M18.805 25.2873L29.61 14.4824L31.123 15.9951L21.83 25.2873L31.123 34.5796L29.61 36.0922L18.805 25.2873Z',
  next: 'M31.77 25.2873L20.965 36.0922L19.453 34.5796L28.745 25.2873L19.453 15.9951L20.965 14.4824L31.77 25.2873Z',
};

const ThinChevron = ({ direction }: { direction: 'prev' | 'next' }) => {
  // The gradient's end stop follows a CSS variable so hover can tint it.
  const gradientId = `hero-chevron-${direction}`;
  return (
    <svg
      viewBox={`0 0 ${BUTTON_SIZE} ${BUTTON_SIZE}`}
      className="size-full"
      aria-hidden
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="14.4824"
          x2="0"
          y2="36.0922"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fff" />
          <stop
            offset="1"
            className="transition-[stop-color] duration-300"
            style={{ stopColor: 'var(--chevron-end)' }}
          />
        </linearGradient>
      </defs>
      <path d={CHEVRON_PATHS[direction]} fill={`url(#${gradientId})`} />
    </svg>
  );
};

const controlButton =
  'flex size-[50.58px] shrink-0 items-center justify-center rounded-full bg-[#89D9F7]/32 backdrop-blur-sm transition-colors duration-300 [--chevron-end:#fff] hover:bg-[#89D9F7]/45 hover:[--chevron-end:#B8E8FA] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B8E8FA]';

export const HeroSliderControls = ({
  active,
  goTo,
  next,
  prev,
  className,
}: {
  active: number;
  goTo: (index: number) => void;
  next: () => void;
  prev: () => void;
  className?: string;
}) => (
  <div
    className={classNames('flex items-center gap-5', className)}
    role="group"
    aria-label="Hero background controls"
  >
    {/* 7px dots on a 13.8px pitch (export geometry); the vertical padding
        is only there to give a usable tap target. */}
    <div className="flex items-center">
      {heroBackgrounds.map((_, i) => {
        const isActive = i === active;
        return (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Show background ${i + 1}`}
            aria-current={isActive}
            className="group flex items-center justify-center px-[3.4px] py-3"
          >
            <span
              className={classNames(
                'block size-[7px] rounded-full transition-colors duration-300',
                isActive
                  ? 'bg-white'
                  : 'bg-white/40 group-hover:bg-white/70'
              )}
            />
          </button>
        );
      })}
    </div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={prev}
        aria-label="Previous background"
        className={controlButton}
      >
        <ThinChevron direction="prev" />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Next background"
        className={controlButton}
      >
        <ThinChevron direction="next" />
      </button>
    </div>
  </div>
);

export default useHeroSlider;
